import { ChevronLeft, Menu } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type ImperativePanelHandle, Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useEditorActiveNode } from '../Editor';
import { EditorTreeUI } from '../components/EditorTree';
import EditorTreeBreadCrumbs from '../components/EditorTreeBreadCrumbs';

type VerticalPanelWithBreadCrumbNavigationProps = {
  children: React.ReactNode;
};

export const VerticalPanelWithBreadCrumbNavigation = ({ children }: VerticalPanelWithBreadCrumbNavigationProps) => {
  const topPanelRef = useRef<ImperativePanelHandle>(null);
  const { activeNodeRoot } = useEditorActiveNode();
  const [childNodeCount, setChildNodeCount] = useState(0);

  const useTabs = useMemo(() => {
    return childNodeCount > 0 && childNodeCount <= 3;
  }, [childNodeCount]);

  const [topPanelCollapsed, setTopPanelCollapsed] = useState(!useTabs);

  const safeExpandPanel = useCallback((size: number) => {
    if (topPanelRef.current) {
      try {
        topPanelRef.current.resize(size);
      } catch (error: unknown) {}
    }
  }, []);

  useLayoutEffect(() => {
    const count = activeNodeRoot?.childrenNodes?.size || 0;
    setChildNodeCount(count);

    const shouldUseTabs = count > 0 && count <= 3;
    setTopPanelCollapsed(!shouldUseTabs);

    if (shouldUseTabs) {
      safeExpandPanel(15);
    } else {
      safeExpandPanel(5);
    }
  }, [activeNodeRoot, safeExpandPanel]);

  const handlePanelExpand = useCallback(() => {
    if (!topPanelRef.current) return;

    try {
      topPanelRef.current.expand(30);
      setTopPanelCollapsed(false);
    } catch (error: unknown) {}
  }, []);

  const handlePanelCollapse = useCallback(() => {
    if (!topPanelRef.current) return;

    try {
      topPanelRef.current.collapse();
      setTopPanelCollapsed(true);
    } catch (error: unknown) {}
  }, []);

  const handleCollapseEvent = useCallback(() => {
    setTopPanelCollapsed(true);
  }, []);

  const handleExpandEvent = useCallback(() => {
    setTopPanelCollapsed(false);
  }, []);

  const breadcrumbProps = { maxItems: 2 };

  const panelSizeSettings = useMemo(
    () => ({
      defaultSize: useTabs ? 15 : 5,
      maxSize: 50,
      minSize: useTabs ? 15 : 5,
      collapsedSize: 5,
    }),
    [useTabs]
  );

  const panelId = `panel-${useTabs ? 'tabs' : 'breadcrumb'}`;

  const renderResizeHandle = () => (
    <PanelResizeHandle
      style={{
        height: '5px',
        border: '1px solid #5e5e5e',
        marginTop: '0.5em',
        marginBottom: '0.5em',
        cursor: 'row-resize',
        position: 'relative',
        display: useTabs ? 'none' : 'block',
      }}
    >
      <Box
        sx={{
          display: topPanelCollapsed ? 'none' : 'block',
          position: 'absolute',
          bottom: '50%',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Tooltip title="Collapse" placement="right">
          <IconButton onClick={handlePanelCollapse} size="small">
            <ChevronLeft sx={{ transform: 'rotate(90deg)', p: 0 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </PanelResizeHandle>
  );

  const renderExpandButton = () => (
    <Button
      onClick={handlePanelExpand}
      className="z-btn-4"
      fullWidth
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        height: '100%',
      }}
    >
      <EditorTreeBreadCrumbs
        breadcrumbProps={breadcrumbProps}
        showAsTabs={false}
        key={`breadcrumb-${activeNodeRoot?.id || 'default'}`}
      />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Menu sx={{ mr: 0.5 }} />
        <span>Menu</span>
      </Box>
    </Button>
  );

  const renderPanelContent = () => {
    if (useTabs) {
      return (
        <EditorTreeBreadCrumbs
          breadcrumbProps={breadcrumbProps}
          showAsTabs={true}
          immediateSelection={true}
          key={activeNodeRoot?.id}
        />
      );
    }

    return topPanelCollapsed ? (
      renderExpandButton()
    ) : (
      <EditorTreeUI
        wrapperProps={{
          overflow: 'auto',
          height: '100%',
        }}
      />
    );
  };

  return (
    <PanelGroup direction="vertical">
      <Panel
        {...panelSizeSettings}
        collapsible={!useTabs}
        ref={topPanelRef}
        onCollapse={handleCollapseEvent}
        onExpand={handleExpandEvent}
        id={panelId}
      >
        {renderPanelContent()}
      </Panel>
      {renderResizeHandle()}
      <Panel>{children}</Panel>
    </PanelGroup>
  );
};
